import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const StoreContext = createContext(null);

const load = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const itemKey = (i) => `${i.product_id}-${i.size}-${i.color || ""}`;

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => load("757_cart", []));
  const [savedItems, setSavedItems] = useState(() => load("757_saved", []));
  const [wishlist, setWishlist] = useState(() => load("757_wishlist", []));
  const [recentlyViewed, setRecentlyViewed] = useState(() => load("757_recent", []));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => localStorage.setItem("757_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("757_saved", JSON.stringify(savedItems)), [savedItems]);
  useEffect(() => localStorage.setItem("757_wishlist", JSON.stringify(wishlist)), [wishlist]);
  useEffect(() => localStorage.setItem("757_recent", JSON.stringify(recentlyViewed)), [recentlyViewed]);

  const addToCart = (product, size, quantity = 1, color = null) => {
    const entry = {
      product_id: product.id,
      slug: product.slug,
      name: product.name,
      price: product.price,
      image: product.images[0],
      size,
      color,
      quantity,
    };
    setCart((prev) => {
      const existing = prev.find((i) => itemKey(i) === itemKey(entry));
      if (existing) {
        return prev.map((i) =>
          itemKey(i) === itemKey(entry) ? { ...i, quantity: Math.min(10, i.quantity + quantity) } : i
        );
      }
      return [...prev, entry];
    });
    toast.success(`${product.name} added to bag`, {
      description: `Size ${size}${color ? ` · ${color}` : ""}`,
    });
    setCartOpen(true);
  };

  const updateQuantity = (item, quantity) => {
    if (quantity < 1) return removeFromCart(item);
    setCart((prev) => prev.map((i) => (itemKey(i) === itemKey(item) ? { ...i, quantity: Math.min(10, quantity) } : i)));
  };

  const removeFromCart = (item) => setCart((prev) => prev.filter((i) => itemKey(i) !== itemKey(item)));

  const clearCart = () => setCart([]);

  const saveForLater = (item) => {
    removeFromCart(item);
    setSavedItems((prev) => (prev.some((s) => itemKey(s) === itemKey(item)) ? prev : [...prev, item]));
    toast("Saved for later");
  };

  const moveToBag = (item) => {
    setSavedItems((prev) => prev.filter((s) => itemKey(s) !== itemKey(item)));
    setCart((prev) => {
      const existing = prev.find((i) => itemKey(i) === itemKey(item));
      if (existing)
        return prev.map((i) => (itemKey(i) === itemKey(item) ? { ...i, quantity: Math.min(10, i.quantity + item.quantity) } : i));
      return [...prev, item];
    });
    toast.success("Moved to bag");
  };

  const removeSaved = (item) => setSavedItems((prev) => prev.filter((s) => itemKey(s) !== itemKey(item)));

  const isWishlisted = (id) => wishlist.some((w) => w.id === id);

  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      if (prev.some((w) => w.id === product.id)) {
        toast("Removed from wishlist");
        return prev.filter((w) => w.id !== product.id);
      }
      toast.success("Saved to wishlist");
      return [
        ...prev,
        { id: product.id, slug: product.slug, name: product.name, price: product.price, image: product.images[0] },
      ];
    });
  };

  const trackViewed = (product) => {
    setRecentlyViewed((prev) => {
      const filtered = prev.filter((p) => p.id !== product.id);
      return [
        { id: product.id, slug: product.slug, name: product.name, price: product.price, image: product.images[0] },
        ...filtered,
      ].slice(0, 8);
    });
  };

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <StoreContext.Provider
      value={{
        cart,
        cartCount,
        cartSubtotal,
        cartOpen,
        setCartOpen,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        savedItems,
        saveForLater,
        moveToBag,
        removeSaved,
        wishlist,
        isWishlisted,
        toggleWishlist,
        recentlyViewed,
        trackViewed,
        itemKey,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => useContext(StoreContext);
